export interface PointRow {
  pointSet: string
  points: string
  side: string
  note: string
}

export interface Needling {
  depth: string
  insertion: string
  retention: string
  manipulation: string
  withdrawal: string
}

export interface Protocol {
  id: string
  name: string
  category: string
  tcmPattern: string
  indicationSummary: string
  points: PointRow[]
  needling: Needling
  frequency: string
  remarks: string
  images?: string[]
}

export const PROTOCOLS: Protocol[] = [
  // Original 5 protocols
  {
    id: 'cfs-001',
    name: 'Chronic Fatigue Syndrome',
    category: 'Internal Medicine',
    tcmPattern: 'Spleen qi deficiency with damp, possible liver qi constraint',
    indicationSummary:
      'Persistent fatigue, poor appetite, heaviness, brain fog.',
    points: [
      {
        pointSet: "Jin's Three Needles",
        points: 'ST36, SP6, REN6',
        side: 'Bilateral / midline',
        note: 'Support qi and blood, transform damp, tonify source qi.',
      },
    ],
    needling: {
      depth: 'Standard body depth according to patient constitution.',
      insertion:
        'Slow, deliberate insertion with focused intention on each point.',
      retention: '20–30 minutes.',
      manipulation:
        'Every 10 minutes, rotate clockwise and counterclockwise 3–5 times in each direction.',
      withdrawal:
        'Withdraw along original angle and apply firm pressure to prevent bleeding/swelling.',
    },
    frequency: '2–3 times per week initially, then reduce as strength returns.',
    remarks:
      'Combine with lifestyle advice, gentle qigong, and appropriate diet.',
  },
  {
    id: 'parkinson-001',
    name: "Parkinson's Disease – Tremor Dominant",
    category: 'Neurology',
    tcmPattern:
      'Liver wind stirring internally on a background of liver–kidney deficiency.',
    indicationSummary:
      'Resting tremor, rigidity, slowness of movement, impaired balance.',
    points: [
      {
        pointSet: "Jin's Three Needles",
        points: 'GB34, LR3, DU20',
        side: 'Bilateral / midline',
        note: 'Calm internal wind, lift clear yang, regulate liver.',
      },
    ],
    needling: {
      depth: 'Standard according to anatomical location and safety.',
      insertion: 'Slow insertion; invite de qi clearly at each point.',
      retention: '25–30 minutes.',
      manipulation:
        'Every 10 minutes, even rotation 3–5 times in both directions, gentle in elderly.',
      withdrawal:
        'Withdraw at same angle; press to avoid subcutaneous bleeding.',
    },
    frequency:
      '2–3 times weekly in early stage; maintain weekly once stabilized.',
    remarks:
      'Combine with rehabilitation exercises, gait training, and calm breathing.',
  },
  {
    id: 'post-stroke-anxiety-001',
    name: 'Post‑Stroke Anxiety and Insomnia',
    category: 'Neurology / Psycho‑Emotional',
    tcmPattern:
      'Heart and gallbladder qi deficiency with phlegm‑heat disturbing shen.',
    indicationSummary: 'Anxiety, palpitations, poor sleep, worry after stroke.',
    points: [
      {
        pointSet: "Jin's Three Needles",
        points: 'HT7, PC6, Yintang',
        side: 'Bilateral / midline',
        note: 'Calm shen, regulate heart, anchor spirit.',
      },
    ],
    needling: {
      depth: 'Light to moderate, especially in frail patients.',
      insertion: 'Slow, reassuring technique; maintain communication.',
      retention: '20–25 minutes.',
      manipulation:
        'Every 10 minutes, soft even rotation; avoid strong stimulation at night.',
      withdrawal: 'Withdraw smoothly, press to avoid bruising.',
    },
    frequency: '2 times per week for 4–6 weeks, then reassess.',
    remarks: 'Add breathing, sleep hygiene, and gentle movement.',
  },
  {
    id: 'dizziness-001',
    name: "Jin's Dizziness Points",
    category: 'Musculoskeletal / Neurologic',
    tcmPattern:
      'Wind-phlegm harassing the head with underlying deficiency varying by case.',
    indicationSummary:
      "Meniere's disease, vertigo, dizziness, all types of headaches especially with dizziness.",
    points: [
      {
        pointSet: "Jin's Four Shen Points",
        points: 'Si Shen group around Baihui (GV20)',
        side: 'Bilateral / scalp',
        note: 'Regulate clear yang, calm shen, stabilize head.',
      },
      {
        pointSet: 'Yintang (Ex‑HN3)',
        points: 'Between the eyebrows',
        side: 'Midline',
        note: 'Guides qi to the head, calms shen, benefits dizziness.',
      },
      {
        pointSet: 'Taiyang (Ex‑HN5)',
        points: 'At the temple region',
        side: 'Bilateral',
        note: 'Relieves temporal headache and dizziness; caution over temporal bone.',
      },
    ],
    needling: {
      depth:
        'Si Shen: subcutaneous to 0.8–1 cun; Yintang: superficial; Taiyang: 0.8–1 cun perpendicular, adjust if bone contacted.',
      insertion:
        'Si Shen: subcutaneous insertion angled outward from GV20; Yintang: downwards toward the nose; Taiyang: perpendicular until de qi.',
      retention: '20–25 minutes depending on severity and constitution.',
      manipulation:
        'Mild even stimulation every 10 minutes, avoid overly strong rotation at scalp and temple.',
      withdrawal:
        'Withdraw each needle along insertion path; at Taiyang, if temporal bone contacted, back out 0.2 cun then adjust.',
    },
    frequency:
      '2–3 times per week during active phase of dizziness, then weekly as maintenance.',
    remarks:
      "Assess blood pressure and inner-ear pathology; coordinate with biomedical care for Meniere's disease.",
  },
  {
    id: 'autism-001',
    name: "Jin's Autism Points",
    category: 'Emotional / Psychological / Pediatrics',
    tcmPattern:
      'Heart and spleen deficiency, phlegm obstruction, and disharmony of shen (varies by child).',
    indicationSummary: 'Pediatric autism spectrum presentations.',
    points: [
      {
        pointSet: 'Yinbai (SP1)',
        points: 'SP1',
        side: 'Bilateral or single as indicated',
        note: 'Regulates spleen, transforms phlegm, focuses shen.',
      },
      {
        pointSet: 'Renzhong (GV26)',
        points: 'GV26',
        side: 'Midline',
        note: 'Arouses brain, opens orifices, regulates central qi.',
      },
      {
        pointSet: 'Tinggong (SI19)',
        points: 'SI19',
        side: 'Bilateral',
        note: 'Benefits the ears and sensory integration, calms spirit.',
      },
    ],
    needling: {
      depth:
        'Very light and shallow in children; adjust by age and constitution.',
      insertion:
        "Gentle, reassuring insertion; often best with child seated on caregiver's lap.",
      retention: 'Short retention, or quick in–out for sensitive children.',
      manipulation: 'Standard gentle needling; avoid aggressive stimulation.',
      withdrawal:
        'Smooth, quick withdrawal with immediate light pressure at each point.',
    },
    frequency:
      '1–3 sessions per week depending on tolerance; focus on long‑term course.',
    remarks:
      'Always integrate with behavioral, speech, and educational therapies; obtain informed consent and explain goals clearly to parents.',
  },
  // New 10 protocols from screenshots
  {
    id: 'facial-muscle',
    name: "Jin's Facial Muscle Points",
    category: 'Musculoskeletal / Neurologic',
    tcmPattern: 'Liver wind with blood stasis affecting facial muscles.',
    indicationSummary: 'Facial spasms, eye tics, mouth twitching.',
    points: [
      {
        pointSet: 'Eye muscle spasm',
        points: 'Sibai (ST 2), Ashi point infra orbital',
        side: 'Affected side',
        note: 'Calm wind, relax muscles around the eye.',
      },
      {
        pointSet: 'Mouth muscle spasm',
        points:
          'Dicang (ST 4) → Jiache (ST 6) thread; Kouheliao (LI 19) → Yingxiang (LI 20) thread',
        side: 'Affected side',
        note: 'Thread technique to relax perioral muscles.',
      },
    ],
    needling: {
      depth: 'Sibai: 0.5 cun subcutaneously; Threading: 0.5–0.8 cun.',
      insertion:
        'Sibai: subcutaneously toward nose or obliquely downward. Threading: insert and thread between points.',
      retention: '30–45 minutes with electro-stimulation.',
      manipulation:
        'Electroacupuncture with dense continuous wave; Note: Electroacupuncture contraindicated at Sibai (ST 2).',
      withdrawal: 'Withdraw along insertion path; apply pressure.',
    },
    frequency: '2–3 times per week until spasms reduce.',
    remarks:
      'Use thinnest needle available for Sibai. Electroacupuncture intensity as strong as patient tolerates.',
  },
  {
    id: 'facial-paralysis',
    name: "Jin's Facial Paralysis Points",
    category: 'Musculoskeletal / Neurologic',
    tcmPattern:
      'Wind-cold or wind-heat invading the channels, qi and blood stagnation.',
    indicationSummary:
      "Facial paralysis (Bell's palsy), mouth deviation, incomplete eyelid closure.",
    points: [
      {
        pointSet: 'Mouth deviation predominant',
        points:
          'Yifeng (TH 17), Jiache (ST 6), Dicang (ST 4), Yingxiang (LI 20)',
        side: 'Affected side',
        note: 'Expel wind, activate collaterals, restore facial movement.',
      },
      {
        pointSet: 'Incomplete eyelid closure',
        points: 'Yangbai (GB 14), Taiyang (Ex-HN5), Sibai (ST 2)',
        side: 'Affected side',
        note: 'Restore eyelid function; often combined with mouth deviation group.',
      },
    ],
    needling: {
      depth:
        'Yifeng: 0.5–1 cun toward eardrum; Dicang/Jiache: 0.5–1 cun subcutaneously toward each other; Yingxiang: 0.3–0.5 cun obliquely upward.',
      insertion:
        'Yifeng: avoid deep insertion to protect facial nerve. Yangbai: subcutaneously through Yuyao. Sibai: obliquely down 0.8–1 cun. Taiyang: perpendicularly 0.8 cun.',
      retention: '20–30 minutes.',
      manipulation:
        'Gentle stimulation; avoid excessive manipulation at Yifeng.',
      withdrawal: 'Withdraw smoothly; press to prevent bruising.',
    },
    frequency:
      'Daily or every other day in acute phase; 2–3 times weekly in recovery.',
    remarks:
      'Contraindication: Yifeng (TH 17) contraindicated for deep insertion. Both point groups often used together as most patients have both conditions.',
  },
  {
    id: 'foot-intelligence',
    name: "Jin's Foot Intelligence Points",
    category: 'Emotional / Psychological / Pediatrics',
    tcmPattern:
      'Kidney essence deficiency affecting brain development and cognition.',
    indicationSummary:
      'Intellectual disability, autism, retarded mental development, speech or language impairment, mutism, neurasthenia, post-stroke hemiplegia.',
    points: [
      {
        pointSet: "Jin's Foot Intelligence",
        points:
          'Yongquan (KI 1), Midpoint between Yongquan and heel, 1 cun medial to midpoint',
        side: 'Bilateral',
        note: 'Tonify kidney, nourish brain, open orifices, benefit intelligence.',
      },
    ],
    needling: {
      depth:
        'Yongquan: 0.8–1.2 cun toward Taichong (LR 3); other points: perpendicular.',
      insertion:
        'Yongquan: needle toward Taichong direction. Other points: needle perpendicularly.',
      retention: '20–30 minutes.',
      manipulation: 'Moderate stimulation; tonifying method.',
      withdrawal: 'Withdraw smoothly; apply pressure.',
    },
    frequency: '2–3 times per week; long-term treatment course.',
    remarks:
      "Part of comprehensive treatment for developmental disorders; combine with other Jin's intelligence point sets.",
  },
  {
    id: 'four-shen',
    name: "Jin's Four Shen Points",
    category: 'Emotional / Psychological / Pediatrics',
    tcmPattern:
      'Shen disturbance with underlying deficiency; varies by condition.',
    indicationSummary:
      'Intellectual disability in children, cerebral palsy, autism, ADD, dizziness, epilepsy, insomnia, amnesia, post-stroke hemiplegia, rhinitis.',
    points: [
      {
        pointSet: "Jin's Four Shen Points",
        points:
          'Qianding (GV 21), Houding (GV 19), 1.5 cun lateral to Baihui (GV 20) bilateral',
        side: 'Scalp / bilateral',
        note: '4 points surrounding vertex, 1.5 cun from Baihui in each direction. Lateral points between Tongtian (BL 7) and Luoque (BL 8).',
      },
    ],
    needling: {
      depth: 'Subcutaneous, 0.5–1 cun.',
      insertion:
        "For intellectual disability/autism/ADD/dizziness: needle outward away from Baihui. For epilepsy/insomnia/amnesia: needle inward toward Baihui. For hemiplegia: needle toward affected side. For rhinitis: needle toward head's front along du channel.",
      retention: '20–30 minutes.',
      manipulation:
        'Gentle subcutaneous manipulation; direction varies by condition.',
      withdrawal: 'Withdraw along insertion path.',
    },
    frequency: '2–3 times per week; long-term for developmental conditions.',
    remarks:
      'Needle direction is critical and varies by indication. Core point set for neurological and developmental conditions.',
  },
  {
    id: 'hand-intelligence',
    name: "Jin's Hand Intelligence Points",
    category: 'Emotional / Psychological / Pediatrics',
    tcmPattern:
      'Heart and pericardium deficiency affecting shen and cognition.',
    indicationSummary:
      'Intellectual disability, mental retardation, ADD, ADHD, autism, hyperactivity, insomnia, epilepsy, hand/wrist pain, numbness, motor and sensory dysfunctions.',
    points: [
      {
        pointSet: "Jin's Hand Intelligence",
        points: 'Laogong (PC 8), Shenmen (HT 7), Neiguan (PC 6)',
        side: 'Bilateral',
        note: 'Calm shen, benefit heart, open orifices, improve cognition and hand function.',
      },
    ],
    needling: {
      depth: 'Standard depth for each point.',
      insertion: 'Standard needling techniques.',
      retention: '20–30 minutes.',
      manipulation: 'Even method; gentle in children.',
      withdrawal: 'Standard withdrawal with pressure.',
    },
    frequency: '2–3 times per week.',
    remarks:
      'Often combined with Four Shen Points and Foot Intelligence Points for comprehensive treatment of developmental disorders.',
  },
  {
    id: 'sciatic',
    name: "Jin's Sciatic Points",
    category: 'Musculoskeletal / Neurologic',
    tcmPattern:
      'Qi and blood stagnation in the Bladder channel; cold-damp or damp-heat obstruction.',
    indicationSummary: 'Sciatic pain.',
    points: [
      {
        pointSet: "Jin's Sciatic Points",
        points:
          "Jin's Zuogudian (sciatic point: end of gluteal crease, 3 cun lateral to posterior midline), Weizhong (BL 40), Kunlun (BL 60)",
        side: 'Affected side',
        note: 'Activate qi and blood in Bladder channel, relieve sciatic pain.',
      },
    ],
    needling: {
      depth:
        "Jin's Zuogudian: 2 cun perpendicular into sciatic nerve; Weizhong/Kunlun: standard depth.",
      insertion:
        'Insert needle perpendicularly into the sciatic nerve to a depth of 2 cun.',
      retention: '20–30 minutes.',
      manipulation:
        'Strong stimulation to achieve de qi radiating down the leg.',
      withdrawal: 'Withdraw smoothly; apply firm pressure.',
    },
    frequency: '2–3 times per week until pain resolves.',
    remarks:
      'Contraindications: During pregnancy—contraindicated. During menstruation—needle with caution.',
  },
  {
    id: 'senile',
    name: "Jin's Senile Points",
    category: 'Anti-Aging / Emotional / Psychological',
    tcmPattern:
      'Kidney essence and marrow deficiency; heart-kidney disharmony.',
    indicationSummary:
      "Dementia, Alzheimer's disease, forgetfulness, cognitive decline.",
    points: [
      {
        pointSet: "Jin's Senile Points",
        points: 'Baihui (GV 20), Renzhong (GV 26), Yongquan (KI 1)',
        side: 'Midline / bilateral',
        note: 'Tonify kidney, nourish marrow, open orifices, benefit brain.',
      },
    ],
    needling: {
      depth:
        'Baihui: 0.5–1 cun parallel; Renzhong: 0.3–0.5 cun upward at angle; Yongquan: 0.8–1.2 cun perpendicular or toward Taichong.',
      insertion:
        'Baihui: parallel needling. Renzhong: needle upwards at an angle. Yongquan: perpendicular or toward Taichong (LR 3).',
      retention: '20–30 minutes.',
      manipulation: 'Tonifying method; gentle stimulation.',
      withdrawal: 'Withdraw smoothly; apply pressure.',
    },
    frequency: '2–3 times per week; long-term treatment.',
    remarks:
      'Part of anti-aging and cognitive support protocols; combine with lifestyle modifications.',
  },
  {
    id: 'shen-settling',
    name: "Jin's Shen Settling Points",
    category: 'Emotional / Psychological / Pediatrics',
    tcmPattern: 'Shen disturbance with liver yang rising or heart fire.',
    indicationSummary:
      'ADD/ADHD, autism, intellectual disability, lack of concentration, cerebral palsy, ocular trembling, vertigo, frontal headache, insomnia, strabismus, diminished visual acuity.',
    points: [
      {
        pointSet: "Jin's Shen Settling Points",
        points:
          '0.5 cun above Yintang (Ex-HN3), 0.5 cun above Yangbai (GB 14) bilateral',
        side: 'Frontal / bilateral',
        note: 'Settle shen, calm liver, benefit eyes and concentration.',
      },
    ],
    needling: {
      depth: '0.3–0.5 cun.',
      insertion: 'Needle downwards 0.3–0.5 cun.',
      retention: '20–30 minutes.',
      manipulation: 'Gentle even method.',
      withdrawal: 'Withdraw smoothly.',
    },
    frequency: '2–3 times per week.',
    remarks:
      'Effective for attention and visual disorders; often combined with Four Shen Points.',
  },
  {
    id: 'three-ankle',
    name: "Jin's Three Ankle Points",
    category: 'Musculoskeletal / Neurologic',
    tcmPattern:
      'Qi and blood stagnation in ankle channels; possible cold-damp obstruction.',
    indicationSummary:
      'All ankle disorders with pain, swelling and limited mobility, sprain, strain, degeneration, heel pain, plantar fasciitis, inverted foot in children with cerebral palsy, gout.',
    points: [
      {
        pointSet: "Jin's Three Ankle Points",
        points: 'Jiexi (ST 41), Kunlun (BL 60), Taixi (KI 3)',
        side: 'Affected side / bilateral',
        note: 'Activate qi and blood around ankle, reduce swelling, relieve pain.',
      },
    ],
    needling: {
      depth: 'Jiexi: 0.8–1 cun; Kunlun and Taixi: needle toward each other.',
      insertion:
        'Jiexi: palpate for indentation and needle 0.8–1 cun. Kunlun and Taixi: needle toward each other.',
      retention: '20–30 minutes.',
      manipulation: 'Even method; moderate stimulation.',
      withdrawal: 'Withdraw smoothly; apply pressure.',
    },
    frequency: '2–3 times per week until symptoms resolve.',
    remarks:
      'Contraindicated during pregnancy. Use with caution during menstruation.',
  },
  {
    id: 'three-arm',
    name: "Jin's Three Arm Points",
    category: 'Musculoskeletal / Neurologic',
    tcmPattern:
      'Qi and blood stagnation in upper limb channels; wind-cold-damp obstruction.',
    indicationSummary:
      "All upper extremity disorders involving joints and soft tissue (tennis elbow, golfer's elbow, wrist or finger pain), post-stroke motor-sensory dysfunction (paralysis, numbness, loss of sensation), exterior wind invasion.",
    points: [
      {
        pointSet: "Jin's Three Arm Points",
        points: 'Quchi (LI 11), Waiguan (TH 5), Hegu (LI 4)',
        side: 'Affected side / bilateral',
        note: 'Activate qi and blood in arm channels, expel wind, relieve pain and restore function.',
      },
    ],
    needling: {
      depth: 'Standard depth for each point.',
      insertion: 'Standard needling techniques.',
      retention: '20–30 minutes.',
      manipulation:
        'Even method; stronger stimulation for pain, gentler for post-stroke.',
      withdrawal: 'Standard withdrawal with pressure.',
    },
    frequency: '2–3 times per week.',
    remarks:
      'Contraindicated during pregnancy. Hegu (LI 4) is a major point for upper limb disorders and exterior conditions.',
  },
  {
    id: 'three-atrophy',
    name: "Jin's Three Atrophy Points",
    category: 'Musculoskeletal / Neurologic',
    tcmPattern:
      'Spleen and stomach deficiency failing to nourish muscles and sinews; liver and kidney deficiency.',
    indicationSummary:
      'Neurodegenerative & chronic neuromuscular disorders (degenerative disorders, motor neuron diseases, myasthenia gravis, polio), acute inflammatory neuromuscular disorders (Guillain-Barré syndrome).',
    points: [
      {
        pointSet: 'Upper extremity wei zheng (atrophy syndrome)',
        points: 'Quchi (LI 11), Chize (LU 5), Hegu (LI 4)',
        side: 'Affected side / bilateral',
        note: 'Tonify qi, nourish sinews, restore upper limb function.',
      },
      {
        pointSet: 'Lower extremity wei zheng (atrophy syndrome)',
        points: 'Zusanli (ST 36), Sanyinjiao (SP 6), Taixi (KI 3)',
        side: 'Affected side / bilateral',
        note: 'Tonify spleen and kidney, nourish sinews, restore lower limb function.',
      },
    ],
    needling: {
      depth: 'Standard depth for each point.',
      insertion: 'Standard needling techniques.',
      retention: '20–30 minutes.',
      manipulation: 'Tonifying method; moderate stimulation.',
      withdrawal: 'Standard withdrawal with pressure.',
    },
    frequency: '2–3 times per week; long-term treatment course.',
    remarks:
      'Contraindicated during pregnancy. Combine with rehabilitation exercises and nutritional support.',
  },
  {
    id: 'three-bi',
    name: "Jin's Three Bi Points",
    category: 'Musculoskeletal / Neurologic / TCM',
    tcmPattern:
      'Heat affecting ying (nutritive) and xue (blood) levels causing closure of orifices.',
    indicationSummary:
      'Bi zheng (closed syndrome): clenched jaws, tight fists, unconsciousness, warm body and cold extremities. Typically occurs in cases of wind stroke or febrile disorders when heat affects the ying and xue levels.',
    points: [
      {
        pointSet: "Jin's Three Bi Points",
        points: 'Shixuan (Ex-UE11), Renzhong (GV 26), Yongquan (KI 1)',
        side: 'Bilateral / midline',
        note: 'Open orifices, clear heat, restore consciousness.',
      },
    ],
    needling: {
      depth:
        'Shixuan: prick to bleed; Renzhong: 0.3–0.5 cun; Yongquan: standard depth.',
      insertion:
        'First, needle and stimulate Shixuan (Ex-UE11). If necessary, use a blood-letting needle to release 2-3 drops of blood.',
      retention: 'Brief retention or quick in-out for Shixuan.',
      manipulation:
        'Strong stimulation to open orifices. Avoid excessive bleeding and do not use moxa.',
      withdrawal: 'Withdraw smoothly; apply pressure.',
    },
    frequency: 'As needed for acute presentation.',
    remarks:
      'Contraindication: Moxa on Shixuan (Ex-UE11) is contraindicated. This is an emergency protocol for acute closed syndrome.',
  },
  {
    id: 'three-brain',
    name: "Jin's Three Brain Points",
    category: 'Emotional / Psychological / Neurologic / Ophthalmologic',
    tcmPattern:
      'Kidney essence deficiency failing to nourish brain and eyes; liver blood deficiency.',
    indicationSummary:
      'Neurodevelopmental & genetic disorders (cerebral palsy, intellectual disability, mental illness in children with or without uncoordinated movements and visual disturbances, spinocerebellar ataxias), visual disorders (optic neuropathy, other visual impairments), occipital headache.',
    points: [
      {
        pointSet: "Jin's Three Brain Points",
        points: 'Naohu (GV 17), Naokong (GB 19) - bilateral',
        side: 'Midline / bilateral',
        note: 'Nourish brain, benefit eyes, calm shen, relieve occipital pain.',
      },
    ],
    needling: {
      depth: '0.8–1.2 cun.',
      insertion: 'Needle downward 0.8–1.2 cun.',
      retention: '20–30 minutes.',
      manipulation: 'Gentle even method; tonifying for deficiency patterns.',
      withdrawal: 'Withdraw smoothly.',
    },
    frequency: '2–3 times per week; long-term for developmental conditions.',
    remarks:
      'Often combined with Four Shen Points for comprehensive neurological treatment.',
  },
  {
    id: 'three-breast',
    name: "Jin's Three Breast Points",
    category: 'Gynecology / Obstetrics / Women',
    tcmPattern:
      'Liver qi stagnation with phlegm accumulation; spleen deficiency failing to transform fluids.',
    indicationSummary:
      'Fibrocystic breast disorders, benign masses in the breast, insufficient milk.',
    points: [
      {
        pointSet: "Jin's Three Breast Points",
        points: 'Rugen (ST 18), Shanzhong (CV 17), Jianjing (GB 21)',
        side: 'Bilateral / midline',
        note: 'Regulate liver qi, transform phlegm, promote lactation, benefit breast.',
      },
    ],
    needling: {
      depth:
        'Rugen: 0.5–0.8 cun obliquely; Shanzhong: 0.5–0.6 cun downward; Jianjing: 0.5–0.8 cun obliquely.',
      insertion:
        'Rugen (ST 18): needle obliquely between the ribs to a depth of 0.5–0.8 cun. Shanzhong (CV 17): needle downward to a depth of 0.5–0.6 cun. Jianjing (GB 21): needle obliquely anteriorly to a depth of 0.5–0.8 cun. Lift and grasp the skin around the point to accurately gauge insertion depth and minimize the risk of pneumothorax.',
      retention: '20–30 minutes.',
      manipulation: 'Even method; gentle stimulation.',
      withdrawal: 'Withdraw smoothly; apply pressure.',
    },
    frequency: '2–3 times per week.',
    remarks:
      'Contraindications: Rugen (ST 18) and Jianjing (GB 21): perpendicular needling is contraindicated. Deep insertion is contraindicated. Contraindicated during pregnancy.',
  },
  {
    id: 'three-cholesterol',
    name: "Jin's Three Cholesterol Points",
    category: 'Cardiovascular / Endocrine / Metabolic',
    tcmPattern:
      'Phlegm-damp obstruction with spleen deficiency; liver qi stagnation affecting lipid metabolism.',
    indicationSummary:
      'High cholesterol, hypertension, and heart disease. Metabolic disorders.',
    points: [
      {
        pointSet: "Jin's Three Cholesterol Points",
        points: 'Neiguan (PC 6), Zusanli (ST 36), Sanyinjiao (SP 6)',
        side: 'Bilateral',
        note: 'Regulate heart and circulation, tonify spleen, transform phlegm-damp, balance metabolism.',
      },
    ],
    needling: {
      depth: 'Standard depth for each point.',
      insertion: 'Standard needling techniques.',
      retention: '30 minutes.',
      manipulation:
        "Apply tonification or sedation techniques as appropriate to the patient's constitution. Electro-acupuncture is generally NOT required.",
      withdrawal: 'Standard withdrawal with pressure.',
    },
    frequency: '2–3 times per week; long-term treatment.',
    remarks:
      'Part of metabolic and cardiovascular support protocols. Combine with dietary modifications and lifestyle changes.',
  },
  {
    id: 'three-discoloration',
    name: "Jin's Three Discoloration Points",
    category: 'Anti-Aging / Health / Beauty / Dermatological',
    tcmPattern:
      'Liver qi stagnation with blood stasis; kidney deficiency affecting skin.',
    indicationSummary: 'Melasma, liver spots, discolorations of the face.',
    points: [
      {
        pointSet: "Jin's Three Discoloration Points",
        points: 'Quanliao (SI 18), Xiaguan (ST 7), Taiyang (Ex-HN5)',
        side: 'Bilateral',
        note: 'Activate blood, dispel stasis, brighten complexion, benefit facial skin.',
      },
    ],
    needling: {
      depth: 'Standard depth for each point.',
      insertion: 'Standard needling techniques.',
      retention: '20–30 minutes.',
      manipulation: 'Electro-acupuncture: use intermittent dense waves.',
      withdrawal: 'Standard withdrawal with pressure.',
    },
    frequency: '2–3 times per week.',
    remarks:
      'Contraindication: To prevent needle breakage during treatment, avoid speaking and mouth movement during treatment.',
  },
  {
    id: 'three-ear',
    name: "Jin's Three Ear Points",
    category: 'Respiratory / Ear Nose Throat',
    tcmPattern:
      'Kidney essence deficiency failing to nourish ears; liver-gallbladder fire rising.',
    indicationSummary:
      "Auditory & developmental hearing disorders (tinnitus, diminished hearing/deafness from any cause including medication or drugs, acuity difficulties in children with cerebral palsy and mentally underdeveloped children). Cranial nerve/orofacial disorders (toothache, TMJ, Bell's palsy, peripheral facial neuritis, pain in the mandibular branch V3 of the trigeminal nerve).",
    points: [
      {
        pointSet: "Jin's Three Ear Points",
        points: 'Tinggong (SI 19), Tinghui (GB 2), Wangu (GB 12)',
        side: 'Bilateral',
        note: 'Benefit ears, open orifices, calm liver, relieve facial pain.',
      },
    ],
    needling: {
      depth:
        'Tinggong/Tinghui: 1–1.2 cun; Wangu: 1–1.2 cun obliquely toward inner ear.',
      insertion:
        'Tinggong (SI 19), Tinghui (GB 2): Have the patient keep their mouth open during insertion. Once the needles are in place, they can gently close their mouth. Slowly insert the needles to 1–1.2 cun deep, until you obtain the de qi sensation. To the patient, the needling sensation may radiate around the ear or present as a dull pressure originating from the periosteum. Be careful not to puncture the superficial temporal artery, especially if a pulse is palpable at the site. Wangu (GB 12): Slowly insert the needle obliquely toward the inner ear to a depth of 1–1.2 cun.',
      retention: '20–30 minutes.',
      manipulation:
        'Deep insertion is essential for stimulating the repair of the auditory nerve. For adults: use a depth of 1–1.2 cun. For infants and children: limit the insertion depth to 1 cun.',
      withdrawal: 'Withdraw smoothly; apply pressure.',
    },
    frequency: '2–3 times per week; long-term for hearing disorders.',
    remarks:
      'Contraindications: Avoid needling into the superficial temporal artery. Contraindicated to use strong electro-stimulation.',
  },
  {
    id: 'three-epilepsy',
    name: "Jin's Three Epilepsy Points",
    category: 'Emotional / Psychological / Neurologic',
    tcmPattern:
      'Phlegm-fire disturbing the heart and brain; liver wind stirring internally.',
    indicationSummary: 'Epilepsy, foot inversion, and foot eversion.',
    points: [
      {
        pointSet: "Jin's Three Epilepsy Points",
        points: 'Neiguan (PC 6), Shenmai (BL 62), Zhaohai (KI 6)',
        side: 'Bilateral',
        note: 'Calm shen, regulate yin and yang qiao mai, control wind, benefit foot alignment.',
      },
    ],
    needling: {
      depth: 'Shenmai/Zhaohai: 1 cun obliquely downward toward sole.',
      insertion:
        'Shenmai (BL 62) and Zhaohai (KI 6): needle obliquely downward toward the sole, to a depth of 1 cun.',
      retention: '20–30 minutes.',
      manipulation: 'Even method; moderate stimulation.',
      withdrawal: 'Withdraw smoothly; apply pressure.',
    },
    frequency: '2–3 times per week; long-term treatment.',
    remarks:
      'These points regulate the yin and yang qiao mai, which are important for epilepsy and foot alignment disorders.',
  },
  {
    id: 'three-eye',
    name: "Jin's Three Eye Points",
    category: 'Ophthalmologic / Eye',
    tcmPattern:
      'Liver blood deficiency failing to nourish eyes; kidney essence deficiency.',
    indicationSummary:
      'Optic nerve atrophy, macular degeneration, retinal detachment, retinitis, ocular fundus diseases, and other visual or eye disorders.',
    points: [
      {
        pointSet: "Jin's Three Eye Points",
        points:
          'Jingming (BL 1), Chengqi (ST 1), The point between the eyeball and the supraorbital ridge when the eyes are looking straight ahead',
        side: 'Affected side / bilateral',
        note: 'Nourish liver and kidney, benefit eyes, restore vision.',
      },
    ],
    needling: {
      depth: '1.2–1.5 cun perpendicular in adults.',
      insertion:
        'All points should be needled perpendicularly to a depth of 1.2–1.5 cun in adults. Gently push the eyeball away to allow safe access. Deep insertion is essential to achieve optimal therapeutic results.',
      retention: '20–30 minutes.',
      manipulation:
        'Stimulation techniques such as needle rotation or electrical stimulation are contraindicated at these points. The only acceptable stimulation is gentle upward stroking of the needle handle using a fingernail.',
      withdrawal:
        'When removing the needles, instruct the patient to relax, and withdraw the needles slowly along the same angle and direction as insertion. Rapid removal may lead to bleeding.',
    },
    frequency: '2–3 times per week.',
    remarks:
      'Contraindications: In some cases, no bleeding is observed during insertion or withdrawal, but delayed bleeding may occur after treatment due to blood accumulation at the orbital margin. To prevent intraorbital bleeding, apply firm pressure at the insertion site for at least five minutes after needle removal. During the needling process, the patient should remain calm and avoid squeezing the eyes shut, blinking excessively, or making sudden movements. If the patient is anxious, uncooperative, or unable to stay still, the treatment should be postponed. Needles should be inserted gently and with minimal force. If resistance is encountered, or if the patient experiences any sharp pain, the needle may be contacting the orbital margin or eyeball—stop immediately and do not continue insertion. Do not needle these points if the patient has a tendency to bleed easily or presents with any coagulation disorders.',
  },
  {
    id: 'three-gallbladder',
    name: "Jin's Three Gallbladder Points",
    category: 'Hepatic / Biliary',
    tcmPattern:
      'Liver-gallbladder damp-heat; qi stagnation in the hypochondriac region.',
    indicationSummary:
      'All hepatic and cholecystic disorders, such as acute or chronic cholecystitis, gallstones, jaundice, hypochondriac pain.',
    points: [
      {
        pointSet: "Jin's Three Gallbladder Points",
        points:
          'Qimen (LR 14) - right side, Riyue (GB 24) - right side, Yanglingquan (GB 34)',
        side: 'Right side / bilateral for Yanglingquan',
        note: 'Clear liver-gallbladder heat, promote bile flow, relieve hypochondriac pain.',
      },
    ],
    needling: {
      depth:
        'Qimen/Riyue: oblique along lower rib edge; Yanglingquan: standard depth.',
      insertion:
        'Qimen (LR 14) and Riyue (GB 24): on the right side only, needle obliquely upward along the lower rib edge, using twirling manipulation WITHOUT lifting or thrusting.',
      retention: '30 minutes or longer.',
      manipulation:
        'Twirling manipulation only for Qimen and Riyue; avoid lifting and thrusting.',
      withdrawal: 'Withdraw smoothly; apply pressure.',
    },
    frequency: '2–3 times per week.',
    remarks:
      'Contraindication: Deep insertion is contraindicated at Qimen and Riyue to avoid pneumothorax.',
  },
  {
    id: 'three-gastric',
    name: "Jin's Three Gastric Points",
    category: 'Gastrointestinal',
    tcmPattern:
      'Spleen-stomach disharmony; stomach qi rebellion or deficiency.',
    indicationSummary:
      'All stomach and intestinal disorders, such as gastric pain, gastritis, gastric ulcer, indigestion, etc.',
    points: [
      {
        pointSet: "Jin's Three Gastric Points",
        points: 'Zhongwan (CV 12), Neiguan (PC 6), Zusanli (ST 36)',
        side: 'Midline / bilateral',
        note: 'Harmonize stomach, descend rebellious qi, tonify spleen, relieve pain.',
      },
    ],
    needling: {
      depth: 'Standard depth for each point.',
      insertion: 'Standard needling techniques.',
      retention: '20–30 minutes.',
      manipulation:
        'Even method; tonifying for deficiency, reducing for excess.',
      withdrawal: 'Standard withdrawal with pressure.',
    },
    frequency: '2–3 times per week.',
    remarks:
      'Contraindication: Deep insertion on Zhongwan (CV 12) is contraindicated.',
  },
  {
    id: 'three-intelligence',
    name: "Jin's Three Intelligence Points",
    category:
      'Anti-Aging / Emotional / Psychological / Neurologic / Ophthalmologic / Pediatrics',
    tcmPattern:
      'Kidney essence deficiency failing to nourish brain; heart-kidney disharmony.',
    indicationSummary:
      "Intellectual disability. Neurological & cognitive disorders (Alzheimer's disease, vascular dementia, senility, stroke sequela). Depression. Physical & sensory symptoms (frontal headache, eye diseases).",
    points: [
      {
        pointSet: "Jin's Three Intelligence Points",
        points: 'Shenting (GV 24), Benshen (GB 13) - bilateral',
        side: 'Midline / bilateral',
        note: 'Nourish brain, benefit intelligence, calm shen, benefit eyes.',
      },
    ],
    needling: {
      depth: '0.8–1 cun in adults; 0.8 cun in children.',
      insertion:
        'Insert the needle subcutaneously towards the face or the top of the head at a depth of 0.8–1 cun in adults and 0.8 cun in children. In most cases, Dr. Jin needles towards the top of the head.',
      retention: '20–30 minutes.',
      manipulation:
        'Electro-acupuncture may be applied to these points as well as manual stimulation to sedate or to tonify.',
      withdrawal: 'Withdraw smoothly.',
    },
    frequency: '2–3 times per week; long-term for cognitive conditions.',
    remarks:
      'Core point set for cognitive and developmental disorders; often combined with Four Shen Points.',
  },
  {
    id: 'three-intestinal',
    name: "Jin's Three Intestinal Points",
    category: 'Gastrointestinal',
    tcmPattern:
      'Spleen-stomach deficiency with damp accumulation; large intestine dysfunction.',
    indicationSummary:
      'All intestinal disorders, such as IBS, ulcerative colitis, dysentery, colitis, constipation, diarrhea, SIBO, etc.',
    points: [
      {
        pointSet: "Jin's Three Intestinal Points",
        points: 'Tianshu (ST 25), Guanyuan (CV 4), Shangjuxu (ST 37)',
        side: 'Bilateral / midline',
        note: 'Regulate intestines, transform damp, tonify spleen, relieve abdominal symptoms.',
      },
    ],
    needling: {
      depth: 'Standard depth for each point.',
      insertion: 'Standard needling techniques.',
      retention: '20–30 minutes.',
      manipulation:
        'Even method; adjust based on excess or deficiency pattern.',
      withdrawal: 'Standard withdrawal with pressure.',
    },
    frequency: '2–3 times per week.',
    remarks: 'Contraindication: Needle with caution during pregnancy.',
  },
  {
    id: 'three-knee',
    name: "Jin's Three Knee Points",
    category: 'Musculoskeletal / Neurologic',
    tcmPattern:
      'Qi and blood stagnation in knee channels; wind-cold-damp bi syndrome.',
    indicationSummary:
      'All acute and chronic knee disorders: knee pain, bone spurs, arthritis, bone-on-bone conditions, acute sprains and strains, and bi zheng (painful obstruction syndrome).',
    points: [
      {
        pointSet: "Jin's Three Knee Points",
        points: 'Xiyan (Ex-LE5), Xuehai (SP 10), Liangqiu (ST 34)',
        side: 'Affected side / bilateral',
        note: 'Activate qi and blood around knee, relieve pain, benefit joint function.',
      },
    ],
    needling: {
      depth: 'Xiyan: 1–1.5 cun toward patella; Xuehai/Liangqiu: 1–1.2 cun.',
      insertion:
        "Xiyan (Ex-LE5): Flex the patient's knee and insert needles bilaterally, angling toward the patella to a depth of 1–1.5 cun, ensuring not to penetrate the articular cavity. Xuehai (SP 10) and Liangqiu (ST 34): Insert needle to a depth of 1–1.2 cun.",
      retention: '20–30 minutes.',
      manipulation: 'Even method; moderate to strong stimulation for pain.',
      withdrawal: 'Withdraw smoothly; apply pressure.',
    },
    frequency: '2–3 times per week until symptoms resolve.',
    remarks:
      'Contraindication: It is contraindicated to insert the needle into the articular cavity for Xiyan (Ex-LE5).',
  },
  {
    id: 'three-leg',
    name: "Jin's Three Leg Points",
    category: 'Gynecology / Obstetrics / Women / Musculoskeletal / Neurologic',
    tcmPattern:
      'Spleen-stomach deficiency; liver blood stagnation; kidney deficiency affecting lower limbs.',
    indicationSummary:
      'Atrophy and motor or sensory dysfunction, particularly in the lower limbs (e.g., post-stroke, muscular atrophy). All gastrointestinal disorders.',
    points: [
      {
        pointSet: "Jin's Three Leg Points",
        points: 'Zusanli (ST 36), Sanyinjiao (SP 6), Taichong (LR 3)',
        side: 'Bilateral',
        note: 'Tonify spleen and stomach, nourish blood, regulate liver, benefit lower limbs.',
      },
    ],
    needling: {
      depth: 'Standard depth for each point.',
      insertion: 'Standard needling techniques.',
      retention: '20–30 minutes.',
      manipulation:
        'Tonifying method for deficiency; even method for mixed patterns.',
      withdrawal: 'Standard withdrawal with pressure.',
    },
    frequency: '2–3 times per week.',
    remarks:
      'Versatile point combination for both lower limb disorders and digestive conditions.',
  },
  {
    id: 'three-lumbar',
    name: "Jin's Three Lumbar Points",
    category: 'Andrological / Men / Musculoskeletal / Neurologic',
    tcmPattern:
      'Kidney deficiency with qi and blood stagnation in the lumbar region.',
    indicationSummary:
      'Lumbar and back pain: due to degeneration, bone spur, sprain and strain, arthritis, general aging, etc. Male sexual dysfunctions: spermatorrhea, impotence, etc.',
    points: [
      {
        pointSet: "Jin's Three Lumbar Points",
        points: 'Shenshu (BL 23), Dachangshu (BL 25), Weizhong (BL 40)',
        side: 'Bilateral',
        note: 'Tonify kidney, activate qi and blood in lumbar region, relieve pain, benefit male function.',
      },
    ],
    needling: {
      depth: '1.2–1.5 cun.',
      insertion: 'Standard needling techniques at a depth of 1.2–1.5 cun.',
      retention: '20–30 minutes.',
      manipulation: 'Even method; moderate to strong stimulation for pain.',
      withdrawal: 'Standard withdrawal with pressure.',
    },
    frequency: '2–3 times per week.',
    remarks:
      'Core point set for lumbar disorders and male reproductive health.',
  },
  {
    id: 'three-neck',
    name: "Jin's Three Neck Points",
    category: 'Musculoskeletal / Neurologic',
    tcmPattern:
      'Qi and blood stagnation in cervical channels; wind-cold-damp obstruction.',
    indicationSummary: 'All neck pain and cervical disorders.',
    points: [
      {
        pointSet: "Jin's Three Neck Points",
        points: 'Tianzhu (BL 10), Bailao (Ex-HN17), Dazhu (BL 11)',
        side: 'Bilateral',
        note: 'Activate qi and blood in cervical region, relieve neck pain and stiffness.',
      },
    ],
    needling: {
      depth:
        'Tianzhu/Bailao: 0.5–1 cun perpendicular; Dazhu: 0.5–1 cun obliquely toward spine.',
      insertion:
        'Tianzhu (BL 10): needle perpendicularly at 0.5–1 cun. Bailao (Ex-HN17): needle perpendicularly at 0.5–1 cun. Dazhu (BL 11): needle obliquely toward the spine at 0.5–1 cun.',
      retention: '20–30 minutes.',
      manipulation: 'Even method; moderate stimulation.',
      withdrawal: 'Withdraw smoothly; apply pressure.',
    },
    frequency: '2–3 times per week.',
    remarks:
      'Contraindications: Tianzhu (BL 10): needle in the direction of the medulla oblongata is contraindicated. Deep insertion over 1 cun is contraindicated.',
  },
  {
    id: 'three-nose',
    name: "Jin's Three Nose Points",
    category: 'Respiratory / Ear Nose Throat',
    tcmPattern:
      'Lung qi deficiency with wind-cold or wind-heat invasion; chronic damp-heat in nasal passages.',
    indicationSummary:
      'All nasal disorders: allergic rhinitis, chronic rhinitis, sinusitis (including frontal sinusitis, ethmoid sinusitis, maxillary sinusitis, and sphenoid sinusitis).',
    points: [
      {
        pointSet: "Jin's Three Nose Points",
        points:
          'Yingxiang (LI 20), Bitong (Ex-HN18), Yintang (Ex-HN3) - for allergic rhinitis, Zanzhu (BL 2) - for chronic rhinitis',
        side: 'Bilateral / midline',
        note: 'Open nasal passages, expel wind, clear heat or cold, benefit breathing.',
      },
    ],
    needling: {
      depth:
        'Yingxiang: 0.5–0.8 cun or 0.5–1 cun; Bitong: 0.5–0.8 cun; Yintang: 0.5–0.8 cun; Zanzhu: 0.5–1 cun.',
      insertion:
        'Yingxiang (LI 20): For allergic rhinitis, acute rhinitis, or wind-cold invasion (clear or white nasal discharge): Needle horizontally toward the nasal wing at a depth of 0.5–0.8 cun. For chronic rhinitis, sinusitis, or prolonged nasal congestion (thick, yellow discharge): Needle obliquely upward along the nasolabial groove, for 0.5–1 cun, through to Shangyingxiang (Ex-HN8). Bitong (Ex-HN18): Needle 0.5–0.8 cun obliquely down towards the transverse plane. Yintang (Ex-HN3) - for allergic rhinitis: Needle subcutaneously and 0.5–0.8 cun downwards to the root of the nose. Zanzhu (BL 2) - for chronic rhinitis: Needle obliquely downward, 0.5–1 cun.',
      retention: '30 minutes.',
      manipulation:
        'Use 1 or 1.5 cun needles. After achieving the de qi sensation, twist the needle to produce a local sensation of soreness and distension. Tearing from the stimulation is common and normal. Manual needle stimulation can be performed every five minutes during the treatment. Electroacupuncture may be used for enhanced effectiveness. Yingxiang (LI 20) and Bitong (Ex-HN18) on the same side can be grouped together and stimulated with a continuous medium-dense wave, alternating the wave type and intensity over a 30-minute session.',
      withdrawal: 'Withdraw smoothly; apply pressure.',
    },
    frequency: '2–3 times per week.',
    remarks:
      'Point selection varies by condition type (allergic vs chronic rhinitis). See technique images for specific point locations.',
    images: [
      'https://cdn.magicpatterns.com/uploads/11JWL44Soura4uCV9XXHiC/image.png',
    ],
  },
  {
    id: 'three-obesity',
    name: "Jin's Three Obesity Points",
    category: 'Anti-Aging / Health / Beauty / Others / Specialty',
    tcmPattern:
      'Spleen deficiency with phlegm-damp accumulation; liver qi stagnation affecting metabolism.',
    indicationSummary: 'Obesity especially around the belly.',
    points: [
      {
        pointSet: "Jin's Three Obesity Points",
        points: 'Zhongwan (CV 12), Zusanli (ST 36), Daimai (GB 26)',
        side: 'Midline / bilateral',
        note: 'Tonify spleen, transform phlegm-damp, regulate metabolism, reduce abdominal fat.',
      },
    ],
    needling: {
      depth:
        'Zhongwan: through muscle to adipose tissue; Daimai: 4 cun or longer along abdominal wall; Zusanli: standard depth.',
      insertion:
        "Zhongwan (CV 12): Insert the needle through the muscle layer to reach the adipose tissue. The insertion depth varies depending on the patient's abdominal fat. Daimai (GB 26): Use a 4-cun or longer needle and insert along the abdominal wall toward the umbilicus. Zusanli (ST 36): Standard needling techniques.",
      retention: '30–40 minutes.',
      manipulation:
        'Apply a dense–disperse electrical stimulation for 30–40 minutes.',
      withdrawal: 'Withdraw smoothly; apply pressure.',
    },
    frequency: '2–3 times per week; long-term treatment.',
    remarks:
      'Contraindication: Deep insertion is contraindicated. Combine with dietary modifications and exercise.',
  },
  {
    id: 'three-respiratory',
    name: "Jin's Three Respiratory Points",
    category: 'Respiratory / Ear Nose Throat',
    tcmPattern:
      'Lung qi deficiency; wei qi weakness failing to protect exterior.',
    indicationSummary:
      'All respiratory conditions: Lung qi deficiency, patients with low immune system who catch colds frequently, allergic rhinitis, asthma, etc.',
    points: [
      {
        pointSet: "Jin's Three Respiratory Points",
        points: 'Dazhu (BL 11), Fengmen (BL 12), Feishu (BL 13)',
        side: 'Bilateral',
        note: 'Tonify lung qi, strengthen wei qi, benefit respiratory function, boost immunity.',
      },
    ],
    needling: {
      depth: '1 cun obliquely toward spine.',
      insertion: 'Needle obliquely 1 cun toward the spine.',
      retention: '20–30 minutes.',
      manipulation: 'Tonifying method; gentle to moderate stimulation.',
      withdrawal: 'Withdraw smoothly; apply pressure.',
    },
    frequency: '2–3 times per week; long-term for immune support.',
    remarks:
      'Contraindication: Perpendicular needling over 0.7 cun is contraindicated to avoid pneumothorax.',
  },
  {
    id: 'three-shoulder',
    name: "Jin's Three Shoulder Points",
    category: 'Musculoskeletal / Neurologic',
    tcmPattern:
      'Qi and blood stagnation in shoulder channels; wind-cold-damp obstruction.',
    indicationSummary:
      'All shoulder disorders: shoulder pain, frozen shoulder, arthritis of the shoulder, etc.',
    points: [
      {
        pointSet: "Jin's Three Shoulder Points",
        points:
          'Jianyu (LI 15) - locate the point in the indentation directly below the center of the acromion; no need to raise the arm. Jianyu (LI 15) - 2 cun anterior; locate without raising the arm. Jianyu (LI 15) - 2 cun posterior; locate without raising the arm.',
        side: 'Affected side',
        note: 'Activate qi and blood around shoulder joint, relieve pain, restore mobility.',
      },
    ],
    needling: {
      depth: '0.8–1.5 cun toward shoulder joint.',
      insertion:
        'For all three points: Use a 1.5–2 cun needle, palpate for indentations and needle toward the shoulder joint at a depth of 0.8–1.5 cun. Do NOT needle into the articular cavity. Needle near the articulation or inferiorly distal towards the fingers.',
      retention: '20–30 minutes.',
      manipulation:
        'Use manual or electro-stimulation along with TDP lamp to enhance results.',
      withdrawal: 'Withdraw smoothly; apply pressure.',
    },
    frequency: '2–3 times per week until symptoms resolve.',
    remarks: 'Contraindication: Do not needle into the articular cavity.',
  },
  {
    id: 'three-spasm',
    name: "Jin's Three Spasm Points",
    category:
      'Cardiovascular / Circulatory / Blood / Musculoskeletal / Neurologic',
    tcmPattern:
      'Liver wind stirring internally; qi and blood stagnation causing muscle spasticity.',
    indicationSummary: 'Post-stroke spasticity.',
    points: [
      {
        pointSet: 'Upper Body',
        points: 'Jiquan (HT 1), Chize (LU 5), Neiguan (PC 6)',
        side: 'Affected side',
        note: 'Relax upper limb spasticity, calm liver wind, activate blood.',
      },
      {
        pointSet: 'Lower Body',
        points:
          'Inguinal point (the point to the right of the inguinal artery), Yinlingquan (SP 9), Sanyinjiao (SP 6)',
        side: 'Affected side',
        note: 'Relax lower limb spasticity, nourish yin, calm wind.',
      },
    ],
    needling: {
      depth: 'Standard depth; inguinal point: 2–3 cun perpendicular.',
      insertion:
        'Standard needling methods. The inguinal point is needled perpendicularly to a depth of 2–3 cun.',
      retention: '20–30 minutes.',
      manipulation: 'Even method; moderate stimulation.',
      withdrawal: 'Withdraw smoothly; apply pressure.',
    },
    frequency: '2–3 times per week; long-term for post-stroke rehabilitation.',
    remarks: 'Contraindication: Avoid arteries while needling.',
  },
  {
    id: 'three-squamous',
    name: "Jin's Three Squamous Points",
    category:
      'Cardiovascular / Circulatory / Blood / Musculoskeletal / Neurologic / Pediatrics',
    tcmPattern:
      'Kidney essence deficiency failing to nourish brain; liver wind with blood stasis.',
    indicationSummary:
      "Stroke and/or sensory or motor dysfunctions (paralysis, hemiplegia, sequelae of cerebrovascular accident, sequelae of brain trauma, abnormality or impairment of voluntary movement, abnormal sensations in the limbs, Parkinson's disease, cerebral atrophy, cerebral arteriosclerosis, facial paralysis, facial sensory impairment). Cognitive dysfunction (intellectual disability or retarded children due to underdeveloped brains, senile dementia). Tinnitus, migraine, deafness.",
    points: [
      {
        pointSet: "Jin's Three Squamous Points",
        points:
          'Point I: 2 cun above the ear apex. Point II: 1 cun anterior to Point I. Point III: 1 cun posterior to Point I.',
        side: 'Bilateral',
        note: 'Nourish brain, calm wind, benefit sensory and motor function. *For kids, adjust the measurement proportionally. The first point is approximately 1.5 cun above the ear apex.',
      },
    ],
    needling: {
      depth: '0.8–1.2 cun subcutaneously downward.',
      insertion:
        'Insert needles at the three points in order. Needle subcutaneously and downward to a depth of 0.8 to 1.2 cun.',
      retention: '20–30 minutes.',
      manipulation:
        'Electro-acupuncture may be applied to these points as well as manual stimulation to sedate or to tonify. Dr. Jin uses a 1.5 cun needle for adults and 1 cun needle for children.',
      withdrawal: 'Withdraw smoothly.',
    },
    frequency: '2–3 times per week; long-term for neurological conditions.',
    remarks:
      'Core scalp point set for stroke rehabilitation and cognitive disorders.',
  },
  {
    id: 'three-thyroid',
    name: "Jin's Three Thyroid Points",
    category:
      'Endocrine / Metabolic / Others / Specialty / Respiratory / Ear Nose Throat',
    tcmPattern:
      'Liver qi stagnation with phlegm accumulation; yin deficiency with fire.',
    indicationSummary:
      'Hyperthyroidism, Hypothyroidism, goiter, thyroid nodules/benign tumors, and other thyroid-related disorders.',
    points: [
      {
        pointSet: "Jin's Three Thyroid Points",
        points: 'Shuitu (ST 10), Tiantu (CV 22), Futu (LI 18)',
        side: 'Bilateral / midline',
        note: 'Regulate thyroid function, transform phlegm, soften nodules.',
      },
    ],
    needling: {
      depth:
        'Shuitu/Futu: 1 cun subcutaneously; Tiantu: 0.2–0.3 cun then 0.8 cun downward.',
      insertion:
        'Shuitu (ST 10): needle subcutaneously in the direction of the thyroid gland using a 1 cun needle. Tiantu (CV 22): needle perpendicularly 0.2–0.3 cun, then angle the needle downwards to a depth of 0.8 cun. The angle and depth of acupuncture must be carefully controlled to prevent injury to the lungs and nearby blood vessels. Futu (LI 18): needle subcutaneously in the direction of the thyroid gland using a 1 cun needle.',
      retention: '20–30 minutes.',
      manipulation:
        'Gentle stimulation; avoid strong manipulation near thyroid.',
      withdrawal: 'Withdraw smoothly; apply pressure.',
    },
    frequency: '2–3 times per week.',
    remarks:
      'Contraindication: Deep insertion and electro-acupuncture are contraindicated.',
  },
  {
    id: 'three-tongue',
    name: "Jin's Three Tongue Points",
    category: 'Dental / Oral / Respiratory / Ear Nose Throat',
    tcmPattern:
      'Heart and spleen deficiency affecting tongue; wind-phlegm obstructing orifices.',
    indicationSummary:
      'Any of the following symptoms (stroke-related or not): Speech-Related Symptoms (aphasia, speech impairment, sudden loss of speech, or hoarseness). Swallowing and Oral Motor Symptoms (difficulty swallowing, drooling). Throat/Inflammatory Symptom (pharyngitis).',
    points: [
      {
        pointSet: "Jin's Three Tongue Points",
        points:
          'Shanglianquan (Ex-HN21), 0.8 cun lateral to Shanglianquan (Ex-HN21) - bilateral',
        side: 'Midline / bilateral',
        note: 'Benefit tongue and speech, open orifices, improve swallowing.',
      },
    ],
    needling: {
      depth: '1–1.5 cun in adults; 0.8–1 cun in children.',
      insertion:
        'Needle perpendicularly upwards through the skin toward the tongue. Insertion depth: 1–1.5 cun in adults; 0.8–1 cun in children.',
      retention: '20–30 minutes.',
      manipulation:
        'Use manual stimulation only—no electrostimulation. Aim for qi sensation at the tongue root, radiating through the mouth and into the cheeks, with warmth, numbness, and throat distention.',
      withdrawal: 'Withdraw smoothly; apply pressure.',
    },
    frequency: '2–3 times per week.',
    remarks: 'Core point set for speech and swallowing disorders.',
    images: [
      'https://cdn.magicpatterns.com/uploads/iAdo9fDaVZudLqRPUVZTE9/image.png',
    ],
  },
  {
    id: 'three-tremor',
    name: "Jin's Three Tremor Points",
    category: 'Musculoskeletal / Neurologic',
    tcmPattern:
      'Liver wind stirring internally; liver-kidney yin deficiency failing to anchor yang.',
    indicationSummary: "Parkinson's disease.",
    points: [
      {
        pointSet: "Jin's Three Tremor Points",
        points:
          "Jin's Four Shen Points, Hegu (LI 4), Taichong (LR 3), Fengchi (GB 20)",
        side: 'Bilateral / scalp',
        note: 'Calm liver wind, nourish yin, anchor yang, benefit motor control.',
      },
    ],
    needling: {
      depth:
        'Four Shen: 0.8–1 cun; Hegu: 1.0–1.5 cun; Taichong: 0.5–1 cun; Fengchi: 0.8–1.2 cun.',
      insertion:
        "Jin's Four Shen Points: Insert the needle horizontally 0.8 to 1 cun toward the scalp periphery. Hegu (LI 4): Insert perpendicularly or obliquely toward Laogong (PC 8), 1.0 to 1.5 cun. Contraindicated during pregnancy. Taichong (LR 3): Insert obliquely toward Yongquan (KI 1), 0.5 to 1 cun, aiming for needle sensation radiating to the sole for optimal effect. Fengchi (GB 20): Insert the needle slightly downward and obliquely toward the tip of the nose, 0.8 to 1.2 cun, or use horizontal insertion toward Fengfu (GV 16).",
      retention: '20–30 minutes.',
      manipulation: 'Even method; moderate stimulation.',
      withdrawal: 'Withdraw smoothly; apply pressure.',
    },
    frequency: '2–3 times per week; long-term treatment.',
    remarks: 'Contraindicated during pregnancy.',
  },
  {
    id: 'three-trigeminal',
    name: "Jin's Three Trigeminal Points",
    category: 'Musculoskeletal / Neurologic',
    tcmPattern:
      'Wind-cold or wind-heat invading facial channels; liver fire rising.',
    indicationSummary: 'Trigeminal neuralgia, TMJ.',
    points: [
      {
        pointSet: "Jin's Three Trigeminal Points",
        points:
          'Taiyang (Ex-HN5), Xiaguan (ST 7), Ashi points. Ophthalmic branch pain: Yuyao (Ex-HN4) and Yangbai (GB 14), or a nearby ashi point. Maxillary branch pain: Sibai (ST 2), or a nearby ashi point. Mandibular branch pain: Daying (ST 5), or a nearby ashi point.',
        side: 'Affected side',
        note: 'Expel wind, clear heat, activate collaterals, relieve facial pain.',
      },
    ],
    needling: {
      depth:
        'Taiyang: 0.8–1 cun; Xiaguan: 1–1.2 cun; Yuyao/Yangbai: 0.3–0.5 cun thread; Sibai: 0.8–1 cun; Daying: 1–1.2 cun.',
      insertion:
        'Taiyang (Ex-HN5): needle to a depth of 0.8–1 cun. Xiaguan (ST 7): needle to a depth of 1–1.2 cun. Yuyao (Ex-HN4): thread 0.3–0.5 cun either toward Yangbai (GB 14) or Sizhukong (TH 23). Yangbai (GB 14): lift the skin to thread downwards 0.3–0.5 cun toward Yuyao (Ex-HN4). Sibai (ST 2): needle inferolaterally to a depth of 0.8–1 cun. Daying (ST 5): needle toward the corner of the mouth for 1–1.2 cun.',
      retention: '30 minutes.',
      manipulation:
        'Retain all needles for 30 minutes and apply moderate manual stimulation every 5 minutes. If using electro-acupuncture, apply a dense, continuous wave at a level the patient can comfortably tolerate. For enhanced therapeutic effect, extend the duration of electrical stimulation. The patient should remain silent and avoid opening the mouth throughout the treatment session to avoid bending or breaking of the needles.',
      withdrawal: 'Withdraw smoothly; apply pressure.',
    },
    frequency: '2–3 times per week.',
    remarks:
      'Contraindications: Deep insertion on Taiyang (Ex-HN5) is contraindicated. Needle should be pulled out about 0.2 cun if it hits the bone.',
  },
  {
    id: 'three-tuo',
    name: "Jin's Three Tuo Points",
    category: 'Musculoskeletal / Neurologic / TCM',
    tcmPattern:
      'Severe exhaustion of yin, yang, qi and blood; collapse of vital qi.',
    indicationSummary:
      'Tuo zheng (abandoned syndrome): Characteristics: profuse sweating, cold extremities, open mouth and closed eyes, fecal and urinary incontinence. Occurs when there is severe exhaustion of yin, yang, qi and blood.',
    points: [
      {
        pointSet: "Jin's Three Tuo Points",
        points: 'Baihui (GV 20), Shenque (CV 8), Renzhong (GV 26)',
        side: 'Midline',
        note: 'Rescue yang, restore consciousness, tonify vital qi.',
      },
    ],
    needling: {
      depth: 'Baihui: moxa; Shenque: moxa only; Renzhong: 0.3–0.5 cun.',
      insertion:
        'Baihui (GV 20): moxa. Shenque (CV 8): moxa with salt or ginger. Renzhong (GV 26): needle obliquely upwards 0.3 to 0.5 cun, or press the area with the fingernail.',
      retention: 'As needed for emergency.',
      manipulation:
        'Strong stimulation at Renzhong; sustained moxa at Baihui and Shenque.',
      withdrawal: 'Withdraw smoothly.',
    },
    frequency: 'Emergency protocol; as needed.',
    remarks:
      'Contraindication: Needling of Shenque (CV 8) is contraindicated. This is an emergency rescue protocol for collapse syndrome.',
  },
  {
    id: 'three-urinary',
    name: "Jin's Three Urinary Points",
    category: 'Urinary / Renal / Water Balance',
    tcmPattern:
      'Kidney qi deficiency; bladder dysfunction; damp-heat in lower jiao.',
    indicationSummary:
      'All urinary disorders, such as incontinence, urgency, infection, inflammation, discomfort, dribbling, anuria, etc.',
    points: [
      {
        pointSet: "Jin's Three Urinary Points",
        points: 'Guanyuan (CV 4), Zhongji (CV 3), Sanyinjiao (SP 6)',
        side: 'Midline / bilateral',
        note: 'Tonify kidney, regulate bladder, clear damp-heat, benefit urination.',
      },
    ],
    needling: {
      depth: 'Standard depth for each point.',
      insertion: 'Standard needling techniques.',
      retention: '20–30 minutes.',
      manipulation: 'Even method; tonifying for deficiency patterns.',
      withdrawal: 'Standard withdrawal with pressure.',
    },
    frequency: '2–3 times per week.',
    remarks: 'Contraindicated during pregnancy.',
  },
  {
    id: 'three-yang',
    name: "Jin's Three Yang Points",
    category: 'Andrological / Men / TCM',
    tcmPattern: 'Kidney yang deficiency; essence and qi insufficiency.',
    indicationSummary:
      'Andrological conditions, such as impotence, spermatorrhea, nocturnal emission, premature ejaculation, infertility, low sperm count, back pain, etc.',
    points: [
      {
        pointSet: "Jin's Three Yang Points",
        points: 'Qihai (CV 6), Guanyuan (CV 4), Shenshu (BL 23)',
        side: 'Midline / bilateral',
        note: 'Tonify kidney yang, strengthen essence, benefit male reproductive function.',
      },
    ],
    needling: {
      depth: 'Standard depth for each point.',
      insertion: 'Standard needling techniques.',
      retention: '20–30 minutes.',
      manipulation: 'Tonifying method; warm needle or moxa may be added.',
      withdrawal: 'Standard withdrawal with pressure.',
    },
    frequency: '2–3 times per week; long-term treatment.',
    remarks: 'Core point set for male reproductive and kidney yang disorders.',
  },
  {
    id: 'three-yin',
    name: "Jin's Three Yin Points",
    category: 'Gynecology / Obstetrics / Women / TCM',
    tcmPattern:
      'Kidney yin deficiency; blood stasis; cold accumulation in lower jiao.',
    indicationSummary:
      'All gynecological conditions, especially irregular menstruation, primary dysmenorrhea, pelvic inflammatory disease, feeling of coldness in the lower abdomen, genital itching, uterine or vaginal prolapse, etc.',
    points: [
      {
        pointSet: "Jin's Three Yin Points",
        points: 'Guanyuan (CV 4), Guilai (ST 29), Sanyinjiao (SP 6)',
        side: 'Midline / bilateral',
        note: 'Tonify kidney yin, regulate menstruation, warm lower jiao, benefit female reproductive function.',
      },
    ],
    needling: {
      depth: 'Standard depth for each point.',
      insertion: 'Standard needling techniques.',
      retention: '20–30 minutes.',
      manipulation: 'Even method; tonifying for deficiency patterns.',
      withdrawal: 'Standard withdrawal with pressure.',
    },
    frequency: '2–3 times per week.',
    remarks:
      'Contraindicated during pregnancy. Core point set for gynecological disorders.',
  },
]
